import { useCallback, useEffect, useRef, useState } from "react";

import noop from "lodash/noop";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";

import { DomainListItem } from "app/modules/project-from-comparison/components/DomainListItem";
import {
    AUTOCOMPLETE_Z_INDEX,
    DEFAULT_COUNTRY_CODE,
    FAST_ANIMATION_DURATION_MS,
} from "app/modules/project-from-comparison/config";
import { useProjectCreation } from "app/modules/project-from-comparison/context";
import { projectCreationDataAutomation } from "app/modules/project-from-comparison/dataAutomation";
import { getExcludesFromDomains } from "app/modules/project-from-comparison/helpers/getExcludesFromDomains";
import { isSameProjectExist } from "app/modules/project-from-comparison/helpers/isSameProjectExist";
import { useExistingProjects } from "app/modules/project-from-comparison/helpers/useExistingProjects";
import { useAnimateListChanges } from "app/modules/project-from-comparison/hooks/useAnimateListChanges";
import { useProjectIcons } from "app/modules/project-from-comparison/hooks/useProjectIcons";
import projectCreatingTrack from "app/modules/project-from-comparison/tracking";
import { ProjectErrors } from "app/modules/project-from-comparison/types";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import { useSimilarSites } from "app/services/sitesService/hooks/useSimilarSites";
import { AutocompleteWebsitesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitesCompareItem";
import type { IWebsitePageResult } from "pro-features/components/AutocompleteWebsites/types";
import type { ISimilarSite } from "pro-features/components/Workspace/Wizard/src/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import * as SC from "./styles";
import { COMPETITIVE_SET_HEADER, ADD_COMPETITOR, AUTOCOMPLETE_PLACEHOLDER } from "./text";

export const DomainList = () => {
    const { country = DEFAULT_COUNTRY_CODE } = proNavigator.getParams();
    const { domains, setErrors, setDomains } = useProjectCreation();
    const { data: similarSitesData = [] } = useSimilarSites(domains[0], { limit: 30 }, { enabled: !!domains[0] });
    const similarSites = similarSitesData as unknown as ISimilarSite[];

    const [isAddingCompetitor, setIsAddingCompetitor] = useState(false);

    const { icons, setIcon } = useProjectIcons(domains);
    const { existingProjects } = useExistingProjects();
    const { translate } = useTranslate();
    const containerRef = useRef<HTMLDivElement>(null);

    useAnimateListChanges(domains);
    const itemsLength = domains.length;
    const competitorsLimit = ActivationComponentAssets.competitorsLimit;

    const isExist = isSameProjectExist(existingProjects, domains, country);
    const excludes = getExcludesFromDomains(domains);

    const onAddDomainClick = () => {
        setIsAddingCompetitor(true);
        projectCreatingTrack.trackAddNewDomainCTAClick();
    };

    const onAddDomain = useCallback(
        (selectedDomain: IWebsitePageResult) => {
            setDomains((prev) => [...prev, selectedDomain.name]);
            setIcon({ name: selectedDomain.name, icon: selectedDomain.icon ?? "" });
        },
        [setDomains, setIcon],
    );

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        setTimeout(() => {
            if (containerRef.current) {
                containerRef.current.scrollTop = containerRef.current.scrollHeight;
            }
        }, FAST_ANIMATION_DURATION_MS);
    }, [domains.length]);

    useEffect(() => {
        setErrors((prevState) => {
            if (isExist === prevState[ProjectErrors.SameCompetitors]) {
                return prevState;
            }

            return { ...prevState, [ProjectErrors.SameCompetitors]: isExist };
        });
    }, [isExist, setErrors]);

    return (
        <SC.Container ref={containerRef}>
            <SC.HeaderContainer>
                <SC.Header data-automation={projectCreationDataAutomation.domainList.header}>
                    {translate(COMPETITIVE_SET_HEADER)}
                </SC.Header>

                <SC.Count>
                    {itemsLength}/{competitorsLimit}
                </SC.Count>
            </SC.HeaderContainer>

            <SC.List
                data-automation={projectCreationDataAutomation.domainList.list}
                $items={itemsLength === 1 ? 1 : itemsLength + 1}
            >
                {domains.map((domain, i) => {
                    const isPrimary = i === 0;
                    const canRemove = itemsLength > 2;
                    const icon = icons[domain];
                    const showVersus = i === 1;

                    return (
                        <DomainListItem
                            key={domain}
                            domain={domain}
                            isPrimary={isPrimary}
                            canRemove={canRemove}
                            excludes={excludes}
                            similarSites={similarSites}
                            icon={icon}
                            setIcon={setIcon}
                            showVersus={showVersus}
                        />
                    );
                })}
            </SC.List>

            {competitorsLimit > itemsLength ? (
                <SC.StyledIconButton
                    onClick={onAddDomainClick}
                    type="flat"
                    iconName="add"
                    iconColor={colorsPalettes.blue[400]}
                    width="max-content"
                >
                    {translate(ADD_COMPETITOR)}
                </SC.StyledIconButton>
            ) : null}

            {isAddingCompetitor ? (
                <AutocompleteWebsitesCompareItem
                    className="AutocompleteWebsitesCreatingProject"
                    onClick={onAddDomain}
                    onCloseButtonClick={noop}
                    similarSites={similarSites}
                    excludes={excludes}
                    autocompleteProps={{
                        placeholder: translate(AUTOCOMPLETE_PLACEHOLDER),
                        onBlur: () => setIsAddingCompetitor(false),
                        zIndex: AUTOCOMPLETE_Z_INDEX,
                        top: "100%",
                    }}
                />
            ) : null}
        </SC.Container>
    );
};
